import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Area, Point } from 'react-easy-crop';
import Cropper from 'react-easy-crop';

import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import { LoaderSpinner } from '@/components/LoaderSpinner/LoaderSpinner';
import Modal from '@/components/Modal/Modal';
import { toast } from '@/utils/toast';

import s from './inputs.module.scss';
import type { PhotoShape } from './usePhotoUpload';

/** The pieces `usePhotoUpload` renders; not used on their own. */
export const AdjustPhotoModal = ({
  src,
  shape,
  onCancel,
  onDone,
}: {
  src: string;
  shape: PhotoShape;
  onCancel: () => void;
  onDone: (file: File) => void;
}) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const [cropping, setCropping] = useState(false);

  const done = useCallback(async () => {
    if (!area || cropping) return;
    setCropping(true);
    try {
      onDone(await getCroppedFile(src, area, 'photo.jpg'));
    } catch {
      toast.error('Couldn’t read the photo. Please try another one.');
    } finally {
      setCropping(false);
    }
  }, [area, cropping, onDone, src]);

  const body = useRef<HTMLDivElement>(null);

  // Enter is Done, wherever focus is — the Modal does not always pull it in, and Enter on the photo
  // button behind (or on a sheet the dialog opened from) would open the picker again. Caught on the
  // way down so a form behind never sees it either. Only this dialog's own buttons (Cancel, Done,
  // the close cross) keep their Enter. Esc is the Modal's own close.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.isComposing) return;
      const portal = body.current?.closest('[data-modal-portal]');
      if (e.target instanceof HTMLButtonElement && portal?.contains(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      void done();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [done]);

  const round = shape === 'round';

  return (
    // Full-screen on a phone: it often opens from a sheet, and a sheet never stacks on a sheet.
    <Modal open size="full" title="Adjust Photo" onClose={onCancel}>
      <div ref={body} className={s.adjust}>
        <div className={s.adjustArea}>
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={round ? 1 : 16 / 9}
            cropShape={round ? 'round' : 'rect'}
            showGrid
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, pixels) => setArea(pixels)}
          />
        </div>
        <input
          type="range"
          className={s.adjustZoom}
          aria-label="Zoom"
          min={1}
          max={3}
          step={0.1}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
        />
        <div className={s.adjustActions}>
          <ButtonOutlinedSmall type="button" onClick={onCancel}>
            Cancel
          </ButtonOutlinedSmall>
          <ButtonSmall type="button" disabled={!area || cropping} onClick={() => void done()}>
            Done
          </ButtonSmall>
        </div>
      </div>
    </Modal>
  );
};

export const PhotoUploadOverlay = ({
  pending,
  dragOver,
}: {
  pending: boolean;
  dragOver: boolean;
}) => {
  if (!pending && !dragOver) return null;
  return (
    <div
      className={clsx(s.photoOverlay, dragOver && s.photoOverlayDrop)}
      role="status"
      aria-label={dragOver ? undefined : 'Uploading Photo'}
    >
      {dragOver ? (
        'Please drop the photo here to use it.'
      ) : (
        <LoaderSpinner size={24} fullHeight={false} label="" />
      )}
    </div>
  );
};

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = url;
  });

const getCroppedFile = async (imageSrc: string, crop: Area, fileName: string): Promise<File> => {
  const image = await createImage(imageSrc);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas is not supported');
  }

  canvas.width = crop.width;
  canvas.height = crop.height;

  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.9),
  );

  if (!blob) {
    throw new Error('Failed to create cropped image');
  }

  return new File([blob], fileName, { type: 'image/jpeg' });
};
